import apiClient from './api';
import {
  InsuranceCompany,
  InsuranceCompanyCreate,
  InsuranceCompanyUpdate,
  PaginatedResponse,
} from '../types';

class CompanyService {
  private readonly basePath = '/companies';

  /**
   * Get list of companies with pagination and search
   */
  async getCompanies(
    skip: number = 0,
    limit: number = 50,
    search?: string
  ): Promise<PaginatedResponse<InsuranceCompany>> {
    const params: any = { skip, limit };
    if (search) {
      params.search = search;
    }

    const response = await apiClient.get<PaginatedResponse<InsuranceCompany>>(
      this.basePath,
      { params }
    );
    return response.data;
  }

  /**
   * Get single company by ID
   * Response includes shipping_address and all department contacts
   */
  async getCompany(id: string): Promise<InsuranceCompany> {
    const response = await apiClient.get<InsuranceCompany>(`${this.basePath}/${id}`);
    
    // Ensure the response includes all expected data
    const company = response.data;
    
    // Ensure departments array exists and each department has contacts array
    if (company.departments) {
      company.departments = company.departments.map(dept => ({
        ...dept,
        contacts: dept.contacts || [],
      }));
    }
    
    return company;
  }

  /**
   * Create new company
   * Includes shipping_address and all department contacts
   */
  async createCompany(data: InsuranceCompanyCreate): Promise<InsuranceCompany> {
    // Ensure departments with contacts are included
    const payload = {
      ...data,
      departments: data.departments || [],
      shipping_address: data.shipping_address || undefined,
    };
    
    const response = await apiClient.post<InsuranceCompany>(this.basePath, payload);
    return response.data;
  }

  /**
   * Update existing company
   * Includes shipping_address and all department contacts
   */
  async updateCompany(id: string, data: InsuranceCompanyUpdate): Promise<InsuranceCompany> {
    // Ensure departments with contacts and shipping_address are included
    const payload = {
      ...data,
      departments: data.departments !== undefined ? data.departments : undefined,
      shipping_address: data.shipping_address !== undefined ? data.shipping_address : undefined,
    };
    
    const response = await apiClient.put<InsuranceCompany>(`${this.basePath}/${id}`, payload);
    return response.data;
  }

  /**
   * Delete company (soft delete)
   */
  async deleteCompany(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}`);
  }

  async downloadImportTemplate(): Promise<void> {
    const response = await apiClient.get(`${this.basePath}/import-template`, { responseType: 'blob' });
    _downloadBlob(response.data, 'company_import_template.xlsx');
  }

  async importFromFile(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${this.basePath}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async exportToExcel(params?: { search?: string; is_active?: boolean }): Promise<void> {
    const response = await apiClient.get(`${this.basePath}/export`, { params, responseType: 'blob' });
    _downloadBlob(response.data, 'companies_export.xlsx');
  }

  async retryImportRow(rawData: Record<string, unknown>): Promise<any> {
    const formData = new FormData();
    const headers  = Object.keys(rawData).join(',');
    const values   = Object.values(rawData).map(v => String(v ?? '')).join(',');
    const blob     = new Blob([`${headers}\n${values}`], { type: 'text/csv' });
    formData.append('file', blob, 'retry.csv');
    const response = await apiClient.post(`${this.basePath}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.results?.[0] ?? response.data;
  }
}

function _downloadBlob(blob: Blob, filename: string): void {
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href  = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const companyService = new CompanyService();
export default companyService;
